import { DriveModeProfile, TransmissionProfile } from '../types';

/**
 * Standard ZF 8HP Drive Mode Calibrations (BMW M340i reference)
 * Natural downshifting calibration:
 * - Eliminates high-RPM jump during normal driving
 * - Prioritizes low RPM, smooth acceleration, and stable gear
 * - Downshifts only when necessary (lugging protection, high throttle demand, or braking)
 */
export const BMW_M340I_DRIVE_MODES: {
  eco: DriveModeProfile;
  comfort: DriveModeProfile;
  sport: DriveModeProfile;
} = {
  eco: {
    lightThrottleUpshiftRPM: 1600,
    mediumThrottleUpshiftRPM: 1800,
    hardThrottleUpshiftRPM: 2600,

    // Natural Downshift Targets:
    lightThrottleDownshiftRPM: 900,     // Low-RPM lugging threshold
    mediumThrottleDownshiftRPM: 1050,   // Moderate throttle downshift target
    hardThrottleDownshiftRPM: 1700,     // High throttle demand downshift target

    maxComfortableDownshiftRPM: 2800,   // Downshift RPM ceiling protection
    downshiftCooldownMs: 2000,          // 2.0s cooldown between downshifts
    downshiftThrottleEnter: 0.65,       // Require 65% throttle for demand downshift
    downshiftThrottleExit: 0.45,        // Hysteresis release at 45% throttle

    shiftDuration: 300,                 // 300ms smooth relaxed gear shift
    gearHoldStrength: 0.2,              // Quick to upshift, resists downshifting
    engineBrakingStrength: 0.3,         // Mild coasting resistance
  },

  comfort: {
    lightThrottleUpshiftRPM: 1900,
    mediumThrottleUpshiftRPM: 2200,
    hardThrottleUpshiftRPM: 3200,

    // Natural Downshift Targets:
    lightThrottleDownshiftRPM: 1000,    // Low-RPM lugging threshold
    mediumThrottleDownshiftRPM: 1150,   // Moderate throttle downshift target
    hardThrottleDownshiftRPM: 1900,     // High throttle demand downshift target

    maxComfortableDownshiftRPM: 3200,   // Downshift RPM ceiling protection
    downshiftCooldownMs: 1500,          // 1.5s cooldown between downshifts
    downshiftThrottleEnter: 0.65,       // Require 65% throttle for demand downshift
    downshiftThrottleExit: 0.45,        // Hysteresis release at 45% throttle

    shiftDuration: 250,                 // 250ms smooth premium automatic shift
    gearHoldStrength: 0.5,              // Balanced cruising vs throttle demand
    engineBrakingStrength: 0.6,         // Natural combustion overrun
  },

  sport: {
    lightThrottleUpshiftRPM: 3500,
    mediumThrottleUpshiftRPM: 4200,
    hardThrottleUpshiftRPM: 6000,

    // Natural Downshift Targets:
    lightThrottleDownshiftRPM: 1500,    // Low-RPM lugging threshold
    mediumThrottleDownshiftRPM: 1800,   // Moderate throttle downshift target
    hardThrottleDownshiftRPM: 3000,     // High throttle demand downshift target

    maxComfortableDownshiftRPM: 4500,   // Downshift RPM ceiling protection
    downshiftCooldownMs: 1000,          // 1.0s cooldown between downshifts
    downshiftThrottleEnter: 0.60,       // Require 60% throttle for sport demand downshift
    downshiftThrottleExit: 0.40,        // Hysteresis release at 40% throttle

    shiftDuration: 180,                 // 180ms rapid performance shift with crisp pop
    gearHoldStrength: 0.95,             // Holds lower gears persistently
    engineBrakingStrength: 1.0,         // Strong decel engine braking & overrun burble
  },
};

/**
 * BMW M340i 8-Speed Automatic Transmission Profile (ZF 8HP51)
 * Real-world OEM gear ratios
 */
export const BMW_M340I_ZF8HP_PROFILE: TransmissionProfile = {
  name: 'BMW M340i ZF 8HP 8-Speed',
  gearRatios: [
    5.25, // 1st
    3.36, // 2nd
    2.17, // 3rd
    1.72, // 4th
    1.32, // 5th
    1.00, // 6th
    0.82, // 7th
    0.64, // 8th
  ],
  finalDriveRatio: 2.81,
  wheelCircumference: 2.11, // meters (standard 245/40R19 tire)
  sourceType: 'FACTORY_OEM',
  notes: 'Factory OEM ZF 8HP51 8-speed automatic ratios for BMW M340i (G20)',
  eco: BMW_M340I_DRIVE_MODES.eco,
  comfort: BMW_M340I_DRIVE_MODES.comfort,
  sport: BMW_M340I_DRIVE_MODES.sport,
};

/**
 * Lamborghini Revuelto 8-Speed Transverse Dual-Clutch Transmission (DCT)
 * Estimated simulation ratios mapped to ANIMA modes (Strada Relaxed, Strada Normal, Sport/Corsa)
 */
export const LAMBORGHINI_REVUELTO_DCT_PROFILE: TransmissionProfile = {
  name: 'Lamborghini Revuelto 8-Speed DCT',
  gearRatios: [
    3.40, // 1st
    2.19, // 2nd
    1.63, // 3rd
    1.29, // 4th
    1.03, // 5th
    0.85, // 6th
    0.72, // 7th
    0.62, // 8th
  ],
  finalDriveRatio: 3.55,
  wheelCircumference: 2.14, // meters (345/30ZR21 high performance rear tire)
  sourceType: 'SIMULATION_ESTIMATE',
  notes: 'Simulation estimate based on Revuelto transverse 8-speed dual-clutch transmission telemetry',
  eco: {
    // Città / Strada Relaxed
    lightThrottleUpshiftRPM: 2200,
    mediumThrottleUpshiftRPM: 2800,
    hardThrottleUpshiftRPM: 4200,
    lightThrottleDownshiftRPM: 1100,
    mediumThrottleDownshiftRPM: 1400,
    hardThrottleDownshiftRPM: 2200,
    maxComfortableDownshiftRPM: 3600,
    downshiftCooldownMs: 2000,
    downshiftThrottleEnter: 0.65,
    downshiftThrottleExit: 0.45,
    shiftDuration: 250,
    gearHoldStrength: 0.3,
    engineBrakingStrength: 0.4,
  },
  comfort: {
    // Strada Normal
    lightThrottleUpshiftRPM: 2800,
    mediumThrottleUpshiftRPM: 3800,
    hardThrottleUpshiftRPM: 5800,
    lightThrottleDownshiftRPM: 1300,
    mediumThrottleDownshiftRPM: 1700,
    hardThrottleDownshiftRPM: 2800,
    maxComfortableDownshiftRPM: 4500,
    downshiftCooldownMs: 1500,
    downshiftThrottleEnter: 0.62,
    downshiftThrottleExit: 0.42,
    shiftDuration: 180,
    gearHoldStrength: 0.6,
    engineBrakingStrength: 0.7,
  },
  sport: {
    // Sport / Corsa Performance (Revving up to 9,250-9,500 RPM scream)
    lightThrottleUpshiftRPM: 4800,
    mediumThrottleUpshiftRPM: 6200,
    hardThrottleUpshiftRPM: 9250,
    lightThrottleDownshiftRPM: 2800,
    mediumThrottleDownshiftRPM: 3800,
    hardThrottleDownshiftRPM: 5500,
    maxComfortableDownshiftRPM: 7500,
    downshiftCooldownMs: 800,
    downshiftThrottleEnter: 0.55,
    downshiftThrottleExit: 0.38,
    shiftDuration: 100, // 100ms ultra-rapid dual-clutch lightning shift
    gearHoldStrength: 1.0,
    engineBrakingStrength: 1.0,
  },
};

/**
 * BMW-Inspired Artificial Profile 1: S85 5.0L V10 7-Speed Close-Ratio
 * Non-OEM artificial simulation transmission tailored for high-revving screaming V10
 */
export const BMW_V10_SMG_ARTIFICIAL_PROFILE: TransmissionProfile = {
  name: 'BMW V10 7-Speed Close-Ratio (Artificial)',
  gearRatios: [
    3.98, // 1st
    2.61, // 2nd
    1.81, // 3rd
    1.39, // 4th
    1.16, // 5th
    1.00, // 6th
    0.83, // 7th
  ],
  finalDriveRatio: 3.62,
  wheelCircumference: 2.08,
  sourceType: 'ARTIFICIAL_SIMULATION',
  notes: 'Artificial close-ratio 7-speed transmission configured specifically for virtual V10 simulation',
  eco: {
    lightThrottleUpshiftRPM: 2000,
    mediumThrottleUpshiftRPM: 2600,
    hardThrottleUpshiftRPM: 3800,
    lightThrottleDownshiftRPM: 1000,
    mediumThrottleDownshiftRPM: 1250,
    hardThrottleDownshiftRPM: 2000,
    maxComfortableDownshiftRPM: 3200,
    downshiftCooldownMs: 1800,
    downshiftThrottleEnter: 0.65,
    downshiftThrottleExit: 0.45,
    shiftDuration: 280,
    gearHoldStrength: 0.3,
    engineBrakingStrength: 0.4,
  },
  comfort: {
    lightThrottleUpshiftRPM: 2600,
    mediumThrottleUpshiftRPM: 3400,
    hardThrottleUpshiftRPM: 5200,
    lightThrottleDownshiftRPM: 1200,
    mediumThrottleDownshiftRPM: 1500,
    hardThrottleDownshiftRPM: 2500,
    maxComfortableDownshiftRPM: 4000,
    downshiftCooldownMs: 1400,
    downshiftThrottleEnter: 0.62,
    downshiftThrottleExit: 0.42,
    shiftDuration: 220,
    gearHoldStrength: 0.6,
    engineBrakingStrength: 0.7,
  },
  sport: {
    lightThrottleUpshiftRPM: 4200,
    mediumThrottleUpshiftRPM: 5600,
    hardThrottleUpshiftRPM: 8100,
    lightThrottleDownshiftRPM: 2400,
    mediumThrottleDownshiftRPM: 3200,
    hardThrottleDownshiftRPM: 4800,
    maxComfortableDownshiftRPM: 6500,
    downshiftCooldownMs: 900,
    downshiftThrottleEnter: 0.55,
    downshiftThrottleExit: 0.38,
    shiftDuration: 130,
    gearHoldStrength: 0.95,
    engineBrakingStrength: 1.0,
  },
};

/**
 * BMW-Inspired Artificial Profile 2: S58 3.0L Twin-Turbo Inline-6 7-Speed Wide-Ratio
 * Non-OEM artificial simulation transmission configured for turbocharged mid-range punch
 */
export const BMW_I6_M_DCT_ARTIFICIAL_PROFILE: TransmissionProfile = {
  name: 'BMW I6 Twin-Turbo 7-Speed Wide-Ratio (Artificial)',
  gearRatios: [
    4.80, // 1st
    3.10, // 2nd
    2.05, // 3rd
    1.50, // 4th
    1.15, // 5th
    0.88, // 6th
    0.68, // 7th
  ],
  finalDriveRatio: 3.15,
  wheelCircumference: 2.11,
  sourceType: 'ARTIFICIAL_SIMULATION',
  notes: 'Artificial wide-spread 7-speed transmission configured for twin-turbo low-end torque cruising',
  eco: {
    lightThrottleUpshiftRPM: 1700,
    mediumThrottleUpshiftRPM: 2000,
    hardThrottleUpshiftRPM: 2800,
    lightThrottleDownshiftRPM: 950,
    mediumThrottleDownshiftRPM: 1100,
    hardThrottleDownshiftRPM: 1750,
    maxComfortableDownshiftRPM: 2900,
    downshiftCooldownMs: 1900,
    downshiftThrottleEnter: 0.65,
    downshiftThrottleExit: 0.45,
    shiftDuration: 260,
    gearHoldStrength: 0.25,
    engineBrakingStrength: 0.35,
  },
  comfort: {
    lightThrottleUpshiftRPM: 2100,
    mediumThrottleUpshiftRPM: 2600,
    hardThrottleUpshiftRPM: 3800,
    lightThrottleDownshiftRPM: 1100,
    mediumThrottleDownshiftRPM: 1300,
    hardThrottleDownshiftRPM: 2100,
    maxComfortableDownshiftRPM: 3400,
    downshiftCooldownMs: 1400,
    downshiftThrottleEnter: 0.65,
    downshiftThrottleExit: 0.45,
    shiftDuration: 200,
    gearHoldStrength: 0.55,
    engineBrakingStrength: 0.65,
  },
  sport: {
    lightThrottleUpshiftRPM: 3600,
    mediumThrottleUpshiftRPM: 4600,
    hardThrottleUpshiftRPM: 6800,
    lightThrottleDownshiftRPM: 1800,
    mediumThrottleDownshiftRPM: 2500,
    hardThrottleDownshiftRPM: 3800,
    maxComfortableDownshiftRPM: 5200,
    downshiftCooldownMs: 950,
    downshiftThrottleEnter: 0.58,
    downshiftThrottleExit: 0.38,
    shiftDuration: 140,
    gearHoldStrength: 0.95,
    engineBrakingStrength: 1.0,
  },
};

/**
 * BMW-Inspired Artificial Profile 3: S65 4.0L High-Rev V8 7-Speed DCT
 * Non-OEM artificial simulation transmission configured for 8,400 RPM naturally aspirated scream
 */
export const BMW_S65_V8_DCT_ARTIFICIAL_PROFILE: TransmissionProfile = {
  name: 'BMW High-Rev V8 7-Speed DCT (Artificial)',
  gearRatios: [
    4.78, // 1st
    3.05, // 2nd
    2.15, // 3rd
    1.67, // 4th
    1.39, // 5th
    1.20, // 6th
    1.00, // 7th
  ],
  finalDriveRatio: 3.15,
  wheelCircumference: 2.08,
  sourceType: 'ARTIFICIAL_SIMULATION',
  notes: 'Artificial 7-speed dual-clutch transmission configured for high-revving 8,400 RPM V8 simulation',
  eco: {
    lightThrottleUpshiftRPM: 1900,
    mediumThrottleUpshiftRPM: 2400,
    hardThrottleUpshiftRPM: 3600,
    lightThrottleDownshiftRPM: 1000,
    mediumThrottleDownshiftRPM: 1200,
    hardThrottleDownshiftRPM: 1900,
    maxComfortableDownshiftRPM: 3100,
    downshiftCooldownMs: 1800,
    downshiftThrottleEnter: 0.65,
    downshiftThrottleExit: 0.45,
    shiftDuration: 260,
    gearHoldStrength: 0.3,
    engineBrakingStrength: 0.4,
  },
  comfort: {
    lightThrottleUpshiftRPM: 2400,
    mediumThrottleUpshiftRPM: 3200,
    hardThrottleUpshiftRPM: 4800,
    lightThrottleDownshiftRPM: 1200,
    mediumThrottleDownshiftRPM: 1450,
    hardThrottleDownshiftRPM: 2400,
    maxComfortableDownshiftRPM: 3800,
    downshiftCooldownMs: 1400,
    downshiftThrottleEnter: 0.62,
    downshiftThrottleExit: 0.42,
    shiftDuration: 200,
    gearHoldStrength: 0.6,
    engineBrakingStrength: 0.7,
  },
  sport: {
    lightThrottleUpshiftRPM: 4000,
    mediumThrottleUpshiftRPM: 5400,
    hardThrottleUpshiftRPM: 8100,
    lightThrottleDownshiftRPM: 2200,
    mediumThrottleDownshiftRPM: 3000,
    hardThrottleDownshiftRPM: 4600,
    maxComfortableDownshiftRPM: 6200,
    downshiftCooldownMs: 900,
    downshiftThrottleEnter: 0.55,
    downshiftThrottleExit: 0.38,
    shiftDuration: 120,
    gearHoldStrength: 0.95,
    engineBrakingStrength: 1.0,
  },
};

export const DEFAULT_TRANSMISSION_PROFILE = BMW_M340I_ZF8HP_PROFILE;

export interface TransmissionConfig {
  gearCount: number;
  ratios: number[];
  finalDriveRatio: number;
  wheelCircumferenceMeters: number;
  autoShiftUpRpmBase: number;
  autoShiftUpRpmAggressive: number;
  autoShiftDownRpm: number;
  shiftDurationMs: number;
  minTimeBetweenShiftsMs: number;
  profile?: TransmissionProfile;
}

export const DEFAULT_TRANSMISSION_CONFIG: TransmissionConfig = {
  gearCount: BMW_M340I_ZF8HP_PROFILE.gearRatios.length,
  ratios: BMW_M340I_ZF8HP_PROFILE.gearRatios,
  finalDriveRatio: BMW_M340I_ZF8HP_PROFILE.finalDriveRatio,
  wheelCircumferenceMeters: BMW_M340I_ZF8HP_PROFILE.wheelCircumference,
  autoShiftUpRpmBase: BMW_M340I_DRIVE_MODES.comfort.lightThrottleUpshiftRPM,
  autoShiftUpRpmAggressive: BMW_M340I_DRIVE_MODES.comfort.hardThrottleUpshiftRPM,
  autoShiftDownRpm: BMW_M340I_DRIVE_MODES.comfort.lightThrottleDownshiftRPM,
  shiftDurationMs: 250,
  minTimeBetweenShiftsMs: 800,
  profile: BMW_M340I_ZF8HP_PROFILE,
};
